import { Box, TextField, TextFieldProps, styled, useTheme } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import DocLink from '../utils/DocLink'
import useResizeObserverFromRef from '../useResizeObserverFromRef'
import { useState } from 'react'
import { MarkdownBaseTypographyVariant } from './MarkDownComponents'

const Container = styled(Box)(() => ({
  flex: 1,
  overflowY: 'hidden'
}))

const MarkdownTextField = styled(TextField)(() => ({
  margin: 0,
  height: '100%',
  '& .MuiInputBase-root': {
    height: '100% !important'
  },
  '& textarea': {
    overflowY: 'scroll !important'
  }
}))

export type MarkDownEditingModeProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  defaultValue?: string
  value?: string
  onChange: (value: string) => void
  height?: string | number
  fitHeight?: boolean
  baseTypographyVariant: MarkdownBaseTypographyVariant
}

function MarkDownEditingMode({
  defaultValue,
  value,
  onChange,
  fitHeight,
  placeholder,
  baseTypographyVariant = 'body2',
  height,
  ...textFieldProps
}: MarkDownEditingModeProps) {
  const intl = useIntl()
  const theme = useTheme()
  placeholder = placeholder || intl.formatMessage({ id: 'workflows.notes.placeholder' })

  // this text area wants to be full height and for some reason, builtin textarea can't reliably "stretch" itself
  // to full height, so we must manually find size of parent InputBase, get its height, remove padding and force
  // it by setting explicit style to textarea element
  const [textAreaHeight, setTextAreaHeight] = useState<number>(0)
  const containerRefSetter = useResizeObserverFromRef<HTMLDivElement>((element) => {
    const textFieldShadow = element.children[0].children[0]
    if (fitHeight && textFieldShadow) {
      setTextAreaHeight(textFieldShadow.clientHeight - theme.spacingValue * 2)
    }
  })

  const textAreaStyles = {
    ...(fitHeight ? { height: `${!textAreaHeight ? 200 : textAreaHeight}px` } : height ? { height } : undefined),
    ...theme.typography[baseTypographyVariant]
  }

  return (
    <Container onWheelCapture={(e) => e.stopPropagation()} ref={containerRefSetter}>
      <MarkdownTextField
        type="text"
        fullWidth
        defaultValue={defaultValue}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        onWheelCapture={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
        variant="filled"
        multiline
        helperText={
          <FormattedMessage
            id="common.markdown.helperText"
            values={{
              docLink: (text) => <DocLink baseUrl="https://www.markdownguide.org/basic-syntax/">{text}</DocLink>
            }}
          />
        }
        minRows={fitHeight ? undefined : 13}
        maxRows={fitHeight ? undefined : 13}
        inputProps={{
          style: {
            ...textAreaStyles
          }
        }}
        {...textFieldProps}
      />
    </Container>
  )
}

export default MarkDownEditingMode
